// Componente de Registro
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { UserPlus, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

function RegisterForm({ onSwitchToLogin, onClose, onRegistrationSuccess }) {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldTouched, setFieldTouched] = useState({
    password: false,
    confirmPassword: false
  });
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    hasLength: false
  });

  const checkPasswordStrength = (password) => {
    const strength = {
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
      hasLength: password.length >= 8
    };
    setPasswordStrength(strength);
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Verificar força da senha
    if (name === 'password') {
      checkPasswordStrength(value);
    }

    // Limpar erro quando usuário começar a digitar
    if (error) setError('');
  };

  const handlePasswordFocus = () => {
    setShowPasswordCriteria(true);
  };

  const handlePasswordBlur = () => {
    setFieldTouched(prev => ({ ...prev, password: true }));
    // Manter critérios visíveis se senha não estiver forte
    if (isPasswordStrong()) {
      setShowPasswordCriteria(false);
    }
  };

  const handleConfirmPasswordBlur = () => {
    setFieldTouched(prev => ({ ...prev, confirmPassword: true }));
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const passwordsMatch = formData.password && formData.confirmPassword && 
                        formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validações no frontend
    if (!isPasswordStrong()) {
      setError('A senha deve atender a todos os critérios de segurança');
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    try {
      const result = await register(
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.name
      );
      
      if (result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Registration successful:', result.user.email);
        }
        
        // Se requer verificação, chamar callback para mostrar tela de verificação
        if (result.requiresVerification) {
          onRegistrationSuccess?.(result);
        } else {
          // Login automático em caso de não precisar verificação
          onClose && onClose();
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCriteria = ({ met, text }) => (
    <div className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      <CheckCircle className={`w-3 h-3 mr-1 ${met ? 'text-green-500' : 'text-gray-300'}`} />
      {text}
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
          <UserPlus className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Criar Conta</h2>
        <p className="text-gray-600 mt-2">
          Cadastre-se para começar a usar
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nome (opcional)
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
              formData.name && formData.name.trim().length >= 2
                ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="Seu nome"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
              formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="seu@email.com"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Senha *
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
              required
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                isPasswordStrong()
                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                  : fieldTouched.password && !isPasswordStrong()
                  ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Sua senha"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {/* Critérios de senha - só mostrar quando em foco ou quando não está forte */}
          {(showPasswordCriteria || (fieldTouched.password && !isPasswordStrong())) && formData.password && (
            <div className="mt-2 space-y-1">
              <PasswordCriteria met={passwordStrength.hasLength} text="Pelo menos 8 caracteres" />
              <PasswordCriteria met={passwordStrength.hasLower} text="Uma letra minúscula" />
              <PasswordCriteria met={passwordStrength.hasUpper} text="Uma letra maiúscula" />
              <PasswordCriteria met={passwordStrength.hasNumber} text="Um número" />
              <PasswordCriteria met={passwordStrength.hasSpecial} text="Um caractere especial (@$!%*?&)" />
            </div>
          )}
          
          {/* Mensagem de sucesso quando senha está forte */}
          {isPasswordStrong() && (
            <div className="mt-2 flex items-center text-green-600">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span className="text-sm">Senha forte ✓</span>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            Confirmar Senha *
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleConfirmPasswordBlur}
              required
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                formData.confirmPassword && passwordsMatch
                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                  : fieldTouched.confirmPassword && formData.confirmPassword && !passwordsMatch
                  ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Confirme sua senha"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {/* Feedback imediato para sucesso, só após touched para erro */}
          {fieldTouched.confirmPassword && formData.confirmPassword && !passwordsMatch && (
            <div className="mt-2 flex items-center text-orange-600">
              <AlertCircle className="w-4 h-4 mr-1" />
              <span className="text-sm">As senhas não coincidem</span>
            </div>
          )}
          {formData.confirmPassword && passwordsMatch && (
            <div className="mt-2 flex items-center text-green-600">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span className="text-sm">Senhas coincidem ✓</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !isPasswordStrong() || !passwordsMatch}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Criando conta...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Criar Conta
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Já tem uma conta?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-green-600 hover:text-green-500 font-medium"
            disabled={loading}
          >
            Entrar
          </button>
        </p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          <p>🔒 Seus dados estão protegidos com criptografia de ponta</p>
        </div>
      </div>
    </div>
  );
}

export default RegisterForm;
